import { IDetails } from "pages/HMI/HMIDashboard/Details/DetailsAPIInterfaces";
import {
    IDefaultUptimeValues,
    IEventsEditionsPending,
    IFailureCode,
    IFailureType,
    IMachineCenter,
    IMQTTBrokerSettings,
} from "../contexts/HMICachedDataContext/HMICachedDataContextInterfaces";
import { IArea, IFunctionalLocation } from "../pages/HMI/HMIDashboard/FailureDetails/FailureDetailsInterfaces";
import { ILanguage } from "components/LanguagesDropdown/LanguagesInterfaces";
import {
    LocalStorageCachedDataKeysCluster,
    LocalStorageCachedDataKeysHMI,
    LocalStorageCorporateBackofficeKeys,
    LocalStorageGlobalKeys,
    LocalStorageTranslationsKeys,
} from "constants/constants";
import { IMachineCentersClusterStatus } from "contexts/ClusterContext/ClusterContextInterfaces";
import {
    IClusterDetails,
    IClusterMachineCenter,
} from "contexts/ClusterCachedDataContext/ClusterCachedDataContextInterfaces";
import { compress, decompress } from "lz-string";
import { ILastTwoShiftsEvents } from "components/HMIUptimeDowntimeBar/HMIUptimeDowntimeBarInterfaces";

export const getCookie = (cookieName: string): string | undefined => {
    const cookieExists = `; ${document.cookie}`.match(`;\\s*${cookieName}=([^;]+)`);
    return cookieExists ? cookieExists[1] : undefined;
};

export const setCookie = (name: string, value: string, expirationDays: number) => {
    const date = new Date();
    const toMilliseconds = 24 * 60 * 60 * 1000;
    date.setTime(date.getTime() + expirationDays * toMilliseconds);
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${value};path=/;${expires};`;
};

export const isTokenAvailable = (): boolean => {
    const token = localStorage.getItem(LocalStorageGlobalKeys.TOKEN);

    return Boolean(token);
};

export const setToken = (token: string) => {
    localStorage.setItem(LocalStorageGlobalKeys.TOKEN, token);
};

export const getToken = (): string | null => {
    return localStorage.getItem(LocalStorageGlobalKeys.TOKEN);
};

export const removeToken = () => {
    localStorage.removeItem(LocalStorageGlobalKeys.TOKEN);
};

export const isUptimeActive = (): boolean => {
    const uptime = localStorage.getItem(LocalStorageCachedDataKeysHMI.UPTIME);

    return Boolean(uptime);
};

export const setUptime = () => {
    localStorage.setItem(LocalStorageCachedDataKeysHMI.UPTIME, "true");
};

export const setPlcDisconnected = () => {
    localStorage.setItem(LocalStorageCachedDataKeysHMI.PLC_DISCONNECTED, "true");
};

export const isPlcDisconnected = (): boolean => {
    const isPlcDisconnected = localStorage.getItem(LocalStorageCachedDataKeysHMI.PLC_DISCONNECTED);

    return Boolean(isPlcDisconnected);
};

export const removePlcDisconnected = () => {
    localStorage.removeItem(LocalStorageCachedDataKeysHMI.PLC_DISCONNECTED);
};

export const setDowntime = () => {
    localStorage.removeItem(LocalStorageCachedDataKeysHMI.UPTIME);
};

export const getCurrentOperator = (): string | undefined => {
    const currentOperator = localStorage.getItem(LocalStorageCachedDataKeysHMI.OPERATOR);

    if (currentOperator) {
        return currentOperator;
    }
};

export const setCurrentOperator = (operator: string | undefined) => {
    if (operator && operator !== "undefined") {
        localStorage.setItem(LocalStorageCachedDataKeysHMI.OPERATOR, operator);
    }
};

export const removeCurrentOperator = () => {
    localStorage.removeItem(LocalStorageCachedDataKeysHMI.OPERATOR);
};

export const setCurrentStartDowntime = (value: number) => {
    localStorage.setItem(LocalStorageCachedDataKeysHMI.CURRENT_START_DOWNTIME, value.toString());
};

export const getCurrentStartDowntime = (): number | undefined => {
    const count = localStorage.getItem(LocalStorageCachedDataKeysHMI.CURRENT_START_DOWNTIME);
    if (count) {
        return +count;
    }
};

export const removeCurrentStartDowntime = () => {
    localStorage.removeItem(LocalStorageCachedDataKeysHMI.CURRENT_START_DOWNTIME);
};

export const getCurrentFailureDetailsStep = (): number | undefined => {
    const failureDetailsStep = localStorage.getItem(LocalStorageCachedDataKeysHMI.FAILURE_DETAILS_STEP);
    if (failureDetailsStep) {
        return +failureDetailsStep;
    }
};

export const setCurrentFailureDetailsStep = (failureDetailsStep: number) => {
    localStorage.setItem(LocalStorageCachedDataKeysHMI.FAILURE_DETAILS_STEP, failureDetailsStep.toString());
};

export const removeCurrentFailureDetailsStep = () => {
    localStorage.removeItem(LocalStorageCachedDataKeysHMI.FAILURE_DETAILS_STEP);
};

export const getCurrentFailureType = (): IFailureType | undefined => {
    const failureType = localStorage.getItem(LocalStorageCachedDataKeysHMI.FAILURE_TYPE);

    if (failureType) {
        return JSON.parse(failureType);
    }
};

export const setCurrentFailureType = (failureType: IFailureType) => {
    localStorage.setItem(LocalStorageCachedDataKeysHMI.FAILURE_TYPE, JSON.stringify(failureType));
};

export const removeCurrentFailureType = () => {
    localStorage.removeItem(LocalStorageCachedDataKeysHMI.FAILURE_TYPE);
};

export const getCurrentArea = (): IArea | undefined => {
    const area = localStorage.getItem(LocalStorageCachedDataKeysHMI.AREA);

    if (area) {
        return JSON.parse(area);
    }
};

export const setCurrentArea = (area: IArea) => {
    localStorage.setItem(LocalStorageCachedDataKeysHMI.AREA, JSON.stringify(area));
};

export const removeCurrentArea = () => {
    localStorage.removeItem(LocalStorageCachedDataKeysHMI.AREA);
};

export const getCurrentFunctionalLocation = (): IFunctionalLocation | undefined => {
    const functionalLocation = localStorage.getItem(LocalStorageCachedDataKeysHMI.FUNCTIONAL_LOCATION);

    if (functionalLocation) {
        return JSON.parse(functionalLocation);
    }
};

export const setCurrentFunctionalLocation = (functionalLocation: IFunctionalLocation) => {
    localStorage.setItem(LocalStorageCachedDataKeysHMI.FUNCTIONAL_LOCATION, JSON.stringify(functionalLocation));
};

export const removeCurrentFunctionalLocation = () => {
    localStorage.removeItem(LocalStorageCachedDataKeysHMI.FUNCTIONAL_LOCATION);
};

export const getCurrentFailureCode = (): IFailureCode | undefined => {
    const failureCode = localStorage.getItem(LocalStorageCachedDataKeysHMI.FAILURE_CODE);

    if (failureCode) {
        return JSON.parse(failureCode);
    }
};

export const getDefaultUptimeValues = (): IDefaultUptimeValues | undefined => {
    const defaultUptimeValues = localStorage.getItem(LocalStorageCachedDataKeysHMI.DEFAULT_UPTIME_VALUES);

    if (defaultUptimeValues) {
        return JSON.parse(defaultUptimeValues);
    }
};

export const setCurrentFailureCode = (failureCode: IFailureCode) => {
    localStorage.setItem(LocalStorageCachedDataKeysHMI.FAILURE_CODE, JSON.stringify(failureCode));
};

export const removeCurrentFailureCode = () => {
    localStorage.removeItem(LocalStorageCachedDataKeysHMI.FAILURE_CODE);
};

export const getCurrentNotes = (): string | undefined => {
    const notes = localStorage.getItem(LocalStorageCachedDataKeysHMI.NOTES);

    if (notes) {
        return notes;
    }
};

export const setCurrentNotes = (notes: string) => {
    localStorage.setItem(LocalStorageCachedDataKeysHMI.NOTES, notes);
};

export const removeCurrentNotes = () => {
    localStorage.removeItem(LocalStorageCachedDataKeysHMI.NOTES);
};

export const resetFailureDetails = () => {
    removeCurrentFailureDetailsStep();
    removeCurrentFailureType();
    removeCurrentArea();
    removeCurrentFunctionalLocation();
    removeCurrentFailureCode();
    removeCurrentNotes();
};

export const removeIsDowntimeDetailsInput = () => {
    localStorage.removeItem(LocalStorageCachedDataKeysHMI.IS_DOWNTIME_DETAILS_INPUT);
};

export const setIsDowntimeDetailsInput = (isInputing: boolean) => {
    localStorage.setItem(LocalStorageCachedDataKeysHMI.IS_DOWNTIME_DETAILS_INPUT, JSON.stringify(isInputing));
};

export const getIsDowntimeDetailsInput = (): boolean | undefined => {
    const eventReportEditing = localStorage.getItem(LocalStorageCachedDataKeysHMI.IS_DOWNTIME_DETAILS_INPUT);

    if (eventReportEditing) {
        return JSON.parse(eventReportEditing);
    }
};

export const setEventReportEditing = (isEditing: boolean) => {
    localStorage.setItem(LocalStorageCachedDataKeysHMI.EVENT_REPORT_EDITING, JSON.stringify(isEditing));
};

export const getEventReportEditing = (): boolean | undefined => {
    const eventReportEditing = localStorage.getItem(LocalStorageCachedDataKeysHMI.EVENT_REPORT_EDITING);

    if (eventReportEditing) {
        try {
            return JSON.parse(eventReportEditing);
        } catch {
            return undefined;
        }
    }
};

export const removeEventReportEditing = () => {
    localStorage.removeItem(LocalStorageCachedDataKeysHMI.EVENT_REPORT_EDITING);
};

export const setEventReportEditData = (data: IDetails) => {
    localStorage.setItem(LocalStorageCachedDataKeysHMI.EVENT_REPORT_EDIT_DATA, JSON.stringify(data));
};

export const getEventReportEditData = (): IDetails | undefined => {
    const eventReportEditingData = localStorage.getItem(LocalStorageCachedDataKeysHMI.EVENT_REPORT_EDIT_DATA);

    if (eventReportEditingData) {
        try {
            return JSON.parse(eventReportEditingData);
        } catch {
            return undefined;
        }
    }
};

export const removeEventReportEditData = () => {
    localStorage.removeItem(LocalStorageCachedDataKeysHMI.EVENT_REPORT_EDIT_DATA);
};

export const setUser = (user_id: number) => {
    localStorage.setItem(LocalStorageGlobalKeys.USER_ID, JSON.stringify(user_id));
};

export const getUser = (): number | undefined => {
    const user_id = localStorage.getItem(LocalStorageGlobalKeys.USER_ID);
    if (user_id) {
        return JSON.parse(user_id);
    }
    return undefined;
};

export const removeUser = () => {
    localStorage.removeItem(LocalStorageGlobalKeys.USER_ID);
};

export const setMachineCenterIdHMI = (machine_center: {
    machine_center_id: number;
    downtime_method_capture_id: number;
}) => {
    localStorage.setItem(LocalStorageCachedDataKeysHMI.MACHINE_CENTER_HMI, JSON.stringify(machine_center));
};

export const getMachineCenterIdHMI = ():
    | { machine_center_id: number; downtime_method_capture_id: number }
    | undefined => {
    const machine_center_hmi = localStorage.getItem(LocalStorageCachedDataKeysHMI.MACHINE_CENTER_HMI);
    if (machine_center_hmi) {
        return JSON.parse(machine_center_hmi);
    }
    return undefined;
};

export const removeMachineCenterIdHMI = () => {
    localStorage.removeItem(LocalStorageCachedDataKeysHMI.MACHINE_CENTER_HMI);
};

export const getHMIDisplayUnit = (): string | undefined => {
    const HMIDisplayUnit = localStorage.getItem(LocalStorageCachedDataKeysHMI.HMI_DISPLAY_UNIT);

    if (HMIDisplayUnit) {
        return JSON.parse(HMIDisplayUnit);
    }
};

export const setHMIDisplayUnit = (displayUnit: string) => {
    localStorage.setItem(LocalStorageCachedDataKeysHMI.HMI_DISPLAY_UNIT, JSON.stringify(displayUnit));
};

export const removeHMIDisplayUnit = () => {
    localStorage.removeItem(LocalStorageCachedDataKeysHMI.HMI_DISPLAY_UNIT);
};

export const setCorporateOnboarding = (isOnboarding: boolean) => {
    localStorage.setItem(LocalStorageCorporateBackofficeKeys.CORPORATE_ONBOARDING, JSON.stringify(isOnboarding));
};

export const isCorporateOnboarding = (): boolean | undefined => {
    const corporate_onboarding = localStorage.getItem(LocalStorageCorporateBackofficeKeys.CORPORATE_ONBOARDING);
    if (corporate_onboarding) {
        return JSON.parse(corporate_onboarding);
    }
    return undefined;
};

export const removeCorporateOnboarding = () => {
    localStorage.removeItem(LocalStorageCorporateBackofficeKeys.CORPORATE_ONBOARDING);
};

export const setCurrentStartBelowTriggerDowntimeCount = (value: number) => {
    localStorage.setItem(LocalStorageCachedDataKeysHMI.BELOW_TRIGGER_DOWNTIME_COUNT, value.toString());
};

export const getCurrentStartBelowTriggerDowntimeCount = (): number | undefined => {
    const belowTriggerDowntimeCount = localStorage.getItem(LocalStorageCachedDataKeysHMI.BELOW_TRIGGER_DOWNTIME_COUNT);

    if (belowTriggerDowntimeCount) {
        return +belowTriggerDowntimeCount;
    }
};

export const removeCurrentStartBelowTriggerDowntimeCount = () => {
    localStorage.removeItem(LocalStorageCachedDataKeysHMI.BELOW_TRIGGER_DOWNTIME_COUNT);
};

export const getAvailableLanguages = (): ILanguage[] | undefined => {
    const languages = JSON.parse(localStorage.getItem(LocalStorageGlobalKeys.LANGUAGES)!);

    if (languages) {
        return languages;
    }
};

export const setAvailableLanguages = (value: ILanguage[]) => {
    localStorage.setItem(LocalStorageGlobalKeys.LANGUAGES, JSON.stringify(value));
};

export const getLastInternetConnectionRecoveryDate = (): string | undefined => {
    const lastInternetConnectionRecoveryDate = localStorage.getItem(
        LocalStorageCachedDataKeysHMI.LAST_INTERNET_CONNECTION_RECOVERY_DATE,
    );

    if (lastInternetConnectionRecoveryDate) {
        return lastInternetConnectionRecoveryDate;
    }
};

export const setLastInternetConnectionRecoveryDate = (value: string) => {
    localStorage.setItem(LocalStorageCachedDataKeysHMI.LAST_INTERNET_CONNECTION_RECOVERY_DATE, JSON.stringify(value));
};

export const setUuidLocalserverDowntimeCode = (value: string) => {
    localStorage.setItem(LocalStorageCachedDataKeysHMI.UUID_LOCALSERVER_DOWNTIME_CODE, value);
};

export const getUuidLocalserverDowntimeCode = (): string | undefined => {
    const uuidLocalserverDowntimeCode = localStorage.getItem(
        LocalStorageCachedDataKeysHMI.UUID_LOCALSERVER_DOWNTIME_CODE,
    );

    if (uuidLocalserverDowntimeCode) {
        return uuidLocalserverDowntimeCode;
    }
};

export const removeUuidLocalserverDowntimeCode = () => {
    localStorage.removeItem(LocalStorageCachedDataKeysHMI.UUID_LOCALSERVER_DOWNTIME_CODE);
};

export const removeUuidLocalserverDowntimeCodeDuringInput = () => {
    localStorage.removeItem(LocalStorageCachedDataKeysHMI.UUID_LOCALSERVER_DOWNTIME_CODE_DURING_INPUT);
};

export const setUuidLocalserverDowntimeCodeDuringInput = (value: string) => {
    localStorage.setItem(LocalStorageCachedDataKeysHMI.UUID_LOCALSERVER_DOWNTIME_CODE_DURING_INPUT, value);
};

export const getUuidLocalserverDowntimeCodeDuringInput = (): string | undefined => {
    const uuidLocalserverDowntimeCodeDuringInput = localStorage.getItem(
        LocalStorageCachedDataKeysHMI.UUID_LOCALSERVER_DOWNTIME_CODE_DURING_INPUT,
    );

    if (uuidLocalserverDowntimeCodeDuringInput) {
        return uuidLocalserverDowntimeCodeDuringInput;
    }
};

export const setNotSelectedOrBelowTriggerEventReportId = (value: number) => {
    localStorage.setItem(LocalStorageCachedDataKeysHMI.NOT_SELECTED_EVENT_REPORT_ID, value.toString());
};

export const getNotSelectedOrBelowTriggerEventReportId = (): number | undefined => {
    const notSelectedOrBelowTriggerEventReportId = localStorage.getItem(
        LocalStorageCachedDataKeysHMI.NOT_SELECTED_EVENT_REPORT_ID,
    );

    if (notSelectedOrBelowTriggerEventReportId) {
        return +notSelectedOrBelowTriggerEventReportId;
    }
};

export const removeNotSelectedOrBelowTriggerEventReportId = () => {
    localStorage.removeItem(LocalStorageCachedDataKeysHMI.NOT_SELECTED_EVENT_REPORT_ID);
};

export const setClusterIdHMI = (cluster: number) => {
    localStorage.setItem(LocalStorageCachedDataKeysCluster.CLUSTER_ID_HMI, JSON.stringify(cluster));
};

export const getClusterIdHMI = (): number | undefined => {
    const cluster_id_hmi = localStorage.getItem(LocalStorageCachedDataKeysCluster.CLUSTER_ID_HMI);
    if (cluster_id_hmi) {
        return +cluster_id_hmi;
    }
    return undefined;
};

export const removeClusterIdHMI = () => {
    localStorage.removeItem(LocalStorageCachedDataKeysCluster.CLUSTER_ID_HMI);
};

export const setMachineCentersClusterStatusLocalStorage = (
    machineCentersClusterStatus: IMachineCentersClusterStatus[],
) => {
    localStorage.setItem(
        LocalStorageCachedDataKeysCluster.MACHINE_CENTERS_CLUSTER_STATUS,
        JSON.stringify(machineCentersClusterStatus),
    );
};

export const getMachineCentersClusterStatusLocalStorage = (): IMachineCentersClusterStatus[] | undefined => {
    const machineCentersClusterStatus = localStorage.getItem(
        LocalStorageCachedDataKeysCluster.MACHINE_CENTERS_CLUSTER_STATUS,
    );
    if (machineCentersClusterStatus) {
        try {
            return JSON.parse(machineCentersClusterStatus);
        } catch {
            return undefined;
        }
    }
    return undefined;
};

export const removeMachineCentersClusterStatusLocalStorage = () => {
    localStorage.removeItem(LocalStorageCachedDataKeysCluster.MACHINE_CENTERS_CLUSTER_STATUS);
};

export const resetLocalStorage = (exceptions: string[]) => {
    const localStorageKeys = [
        ...Object.values(LocalStorageCachedDataKeysCluster),
        ...Object.values(LocalStorageCorporateBackofficeKeys),
        ...Object.values(LocalStorageGlobalKeys),
        ...Object.values(LocalStorageCachedDataKeysHMI),
    ];
    localStorageKeys.forEach((key) => {
        if (!exceptions.includes(key)) {
            localStorage.removeItem(key);
        }
    });
};

export const resetLocalStorageTranslation = () => {
    const localStorageKeys = [...Object.values(LocalStorageTranslationsKeys)];
    localStorageKeys.forEach((key) => {
        localStorage.removeItem(key);
    });
};

export const getMachineCenterHMIStore = (): IMachineCenter | undefined => {
    const machineCenterHmiStore = getLocalStorageItem(LocalStorageCachedDataKeysHMI.MACHINE_CENTER);
    if (machineCenterHmiStore) {
        try {
            return JSON.parse(machineCenterHmiStore);
        } catch {
            return undefined;
        }
    }
    return undefined;
};

export const getMachineCentersClusterStore = (): IClusterMachineCenter[] | undefined => {
    const machineCentersClusterStore = getLocalStorageItem(LocalStorageCachedDataKeysCluster.MACHINE_CENTERS_CLUSTER);
    if (machineCentersClusterStore) {
        try {
            return JSON.parse(machineCentersClusterStore);
        } catch {
            return undefined;
        }
    }
    return undefined;
};

export const setLocalStorageItem = (key: string, value: string) => {
    const compressData = compress(value);

    localStorage.setItem(key, compressData);
};

export const getLocalStorageItem = (key: string) => {
    const value = localStorage.getItem(key);
    if (value) {
        try {
            // Attempts to parse as JSON
            JSON.parse(value);
            // If successful, it's a JSON string, return as is
            return value;
        } catch (e) {
            // If parsing fails, assume it's compressed and try to decompress, if not returns null
            return decompress(value);
        }
    }
    return null;
};

export const getDefaultUptimeValuesHMIStore = (): IDefaultUptimeValues | undefined => {
    const defaultUptimeValues = getLocalStorageItem(LocalStorageCachedDataKeysHMI.DEFAULT_UPTIME_VALUES);
    if (defaultUptimeValues) {
        try {
            return JSON.parse(defaultUptimeValues);
        } catch {
            return undefined;
        }
    }
};

export const getLastTwoShiftEventsHMIStore = (): ILastTwoShiftsEvents | undefined => {
    const lastTwoShiftEvents = getLocalStorageItem(LocalStorageCachedDataKeysHMI.LAST_TWO_SHIFT_EVENTS);
    if (lastTwoShiftEvents) {
        try {
            return JSON.parse(lastTwoShiftEvents);
        } catch {
            return undefined;
        }
    }
};

export const getFailureTypesHMIStore = (): IFailureType[] | undefined => {
    const failureTypes = getLocalStorageItem(LocalStorageCachedDataKeysHMI.FAILURE_TYPES);
    if (failureTypes) {
        try {
            return JSON.parse(failureTypes);
        } catch {
            return undefined;
        }
    }
};

export const getMQTTBrokerSettingsHMIStore = (): IMQTTBrokerSettings[] | undefined => {
    const mqttBrokerSettings = getLocalStorageItem(LocalStorageCachedDataKeysHMI.MQTT_BROKER_SETTINGS);
    if (mqttBrokerSettings) {
        try {
            return JSON.parse(mqttBrokerSettings);
        } catch {
            return undefined;
        }
    }
};

export const getClusterDetailsClusterStore = (): IClusterDetails | undefined => {
    const clusterDetails = localStorage.getItem(LocalStorageCachedDataKeysCluster.CLUSTER_DETAILS);
    if (clusterDetails) {
        try {
            return JSON.parse(clusterDetails);
        } catch {
            return undefined;
        }
    }
};

export const getDataLastUpdatedDateHMIStore = (): Date | undefined => {
    const dataLastUpdatedDate = getLocalStorageItem(LocalStorageCachedDataKeysHMI.DATA_LAST_UPDATED);
    if (dataLastUpdatedDate) {
        try {
            return JSON.parse(dataLastUpdatedDate);
        } catch {
            return undefined;
        }
    }
};

export const getEditionsPendingHMIStore = (): IEventsEditionsPending | undefined => {
    const editionsPending = getLocalStorageItem(LocalStorageCachedDataKeysHMI.EDITIONS_PENDING);
    if (editionsPending) {
        try {
            return JSON.parse(editionsPending);
        } catch {
            return undefined;
        }
    }
};
